import { chakra } from '@chakra-ui/react';
import React from 'react';

/**
 * Footer quick links config.
 *
 * Why this file:
 * - Central place to maintain icon + label + url
 * - Easy to reuse in other places (header/side menu/etc.)
 * - Icons use `currentColor`, so they follow link color/hover styles
 */

export type FooterQuickLinkIconId = 'x' | 'discord' | 'docs';

export type FooterQuickLinkConfig = {
  icon: FooterQuickLinkIconId;
  label: string;
  href: string;
};

/**
 * Single source of truth for quick links.
 * If you need to reuse these links elsewhere, import this constant.
 */
export const FOOTER_QUICK_LINKS: ReadonlyArray<FooterQuickLinkConfig> = [
  { icon: 'x', label: 'Twitter', href: 'https://x.com/bouncebit' },
  { icon: 'discord', label: 'Discord', href: 'https://discord.gg/bouncebit' },
  { icon: 'docs', label: 'Docs', href: 'https://docs.bouncebit.io/' },
];

/**
 * Icons provided by design.
 *
 * NOTE:
 * - Inline SVG to avoid touching sprite/icon pipeline.
 * - All paths are `currentColor`.
 */
const FooterIconX = (props: React.ComponentProps<typeof chakra.svg>) => {
  return (
    <chakra.svg
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      boxSize={ 5 }
      flexShrink={ 0 }
      aria-hidden="true"
      { ...props }
    >
      <path
        d="M14.5027 2.9165H16.9044L11.6575 8.91736L17.83 17.0832H12.997L9.21164 12.1306L4.88031 17.0832H2.47725L8.08924 10.6646L2.16797 2.9165H7.12366L10.5453 7.4433L14.5027 2.9165ZM13.6599 15.6447H14.9906L6.40056 4.27941H4.97251L13.6599 15.6447Z"
        fill="currentColor"
      />
    </chakra.svg>
  );
};

const FooterIconDiscord = (props: React.ComponentProps<typeof chakra.svg>) => {
  return (
    <chakra.svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      boxSize={ 5 }
      flexShrink={ 0 }
      aria-hidden="true"
      { ...props }
    >
      <path
        d="M16.0742 4.45014C14.9244 3.92097 13.7106 3.54556 12.4638 3.3335C12.2932 3.64011 12.1388 3.95557 12.0013 4.27856C10.6732 4.07738 9.32261 4.07738 7.99451 4.27856C7.85694 3.9556 7.70257 3.64014 7.53203 3.3335C6.28441 3.54735 5.06981 3.92365 3.91889 4.45291C1.63401 7.85128 1.01462 11.1652 1.32431 14.4322C2.6624 15.426 4.16009 16.1819 5.7523 16.6668C6.11082 16.1821 6.42806 15.6678 6.70066 15.1295C6.18289 14.9351 5.68315 14.6953 5.20723 14.4128C5.33249 14.3215 5.45499 14.2274 5.57336 14.136C6.95819 14.7907 8.46965 15.1302 9.99997 15.1302C11.5303 15.1302 13.0418 14.7907 14.4266 14.136C14.5463 14.2343 14.6688 14.3284 14.7927 14.4128C14.3159 14.6958 13.8152 14.9361 13.2965 15.1309C13.5688 15.669 13.8861 16.1828 14.2449 16.6668C15.8385 16.1838 17.3373 15.4283 18.6756 14.4335C19.039 10.645 18.0549 7.36145 16.0742 4.45014ZM7.09294 12.423C6.22992 12.423 5.51693 11.6357 5.51693 10.6671C5.51693 9.69852 6.20514 8.90427 7.09019 8.90427C7.97524 8.90427 8.68272 9.69852 8.66758 10.6671C8.65244 11.6357 7.97248 12.423 7.09294 12.423ZM12.907 12.423C12.0426 12.423 11.3324 11.6357 11.3324 10.6671C11.3324 9.69852 12.0206 8.90427 12.907 8.90427C13.7934 8.90427 14.4954 9.69852 14.4803 10.6671C14.4651 11.6357 13.7865 12.423 12.907 12.423Z"
        fill="currentColor"
      />
    </chakra.svg>
  );
};

const FooterIconDocs = (props: React.ComponentProps<typeof chakra.svg>) => {
  return (
    <chakra.svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      boxSize={ 5 }
      flexShrink={ 0 }
      aria-hidden="true"
      { ...props }
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.3119 1.18844C11.4584 1.33489 11.4584 1.57059 11.4584 2.04199V3.55776L11.4584 3.59951V3.59954V3.59955V3.59956C11.4583 4.19844 11.4583 4.7097 11.5132 5.11814C11.5718 5.55402 11.7031 5.96511 12.0357 6.29773C12.3683 6.63035 12.7794 6.76161 13.2153 6.82021C13.6237 6.87512 14.135 6.8751 14.7339 6.87507H14.7339L16.2917 6.87507C16.7631 6.87507 16.9988 6.87507 17.1453 7.02151C17.2917 7.16796 17.2917 7.40366 17.2917 7.87507V13.3794V13.3794V13.3794V13.3794C17.2917 14.5191 17.2917 15.4377 17.1946 16.1601C17.0938 16.9102 16.878 17.5418 16.3764 18.0434C15.8748 18.545 15.2433 18.7607 14.4932 18.8616C13.7707 18.9587 12.8521 18.9587 11.7124 18.9587H8.28765C7.14799 18.9587 6.22939 18.9587 5.50692 18.8616C4.75683 18.7607 4.12526 18.545 3.62367 18.0434C3.12207 17.5418 2.90632 16.9102 2.80548 16.1601C2.70834 15.4376 2.70836 14.5191 2.70837 13.3794V13.3794V6.62126V6.62125C2.70836 5.4816 2.70834 4.563 2.80548 3.84053C2.90632 3.09044 3.12207 2.45888 3.62367 1.95728C4.12526 1.45569 4.75683 1.23994 5.50692 1.1391C6.22939 1.04196 7.14799 1.04198 8.28765 1.04199L10.4584 1.04199C10.9298 1.04199 11.1655 1.04199 11.3119 1.18844ZM17.1807 5.62076C17.1816 5.62281 17.18 5.62507 17.1778 5.62507H14.7757C14.1234 5.62507 13.6971 5.62374 13.3819 5.58136C13.0846 5.54139 12.9813 5.4755 12.9196 5.41385C12.8579 5.35219 12.7921 5.24885 12.7521 4.95158C12.7097 4.63633 12.7084 4.21001 12.7084 3.55776V1.15606C12.7084 1.1537 12.7108 1.1521 12.7129 1.153C12.9852 1.26577 13.1917 1.47349 13.3831 1.66595L13.4301 1.71321L16.6205 4.90356L16.6677 4.95063C16.8602 5.14199 17.0679 5.34851 17.1807 5.62076ZM6.66671 9.37507C6.32153 9.37507 6.04171 9.65489 6.04171 10.0001C6.04171 10.3452 6.32153 10.6251 6.66671 10.6251H13.3334C13.6786 10.6251 13.9584 10.3452 13.9584 10.0001C13.9584 9.65489 13.6786 9.37507 13.3334 9.37507H6.66671ZM6.66671 11.8751C6.32153 11.8751 6.04171 12.1549 6.04171 12.5001C6.04171 12.8452 6.32153 13.1251 6.66671 13.1251H13.3334C13.6786 13.1251 13.9584 12.8452 13.9584 12.5001C13.9584 12.1549 13.6786 11.8751 13.3334 11.8751H6.66671ZM6.66671 14.3751C6.32153 14.3751 6.04171 14.65489 6.04171 15.0001C6.04171 15.3452 6.32153 15.6251 6.66671 15.6251H12.0834C12.4286 15.6251 12.7084 15.3452 12.7084 15.0001C12.7084 14.6549 12.4286 14.3751 12.0834 14.3751H6.66671Z"
        fill="currentColor"
      />
    </chakra.svg>
  );
};

export const FooterQuickLinkIcon = ({ icon, ...props }: { icon: FooterQuickLinkIconId } & React.ComponentProps<typeof chakra.svg>) => {
  switch (icon) {
    case 'x':
      return <FooterIconX { ...props }/>;
    case 'discord':
      return <FooterIconDiscord { ...props }/>;
    case 'docs':
      return <FooterIconDocs { ...props }/>;
    default:
      return null;
  }
};
